import { CALCULATORS } from "./registry";

/**
 * Canonical calculator taxonomy — the single source of truth for how calculators are grouped and
 * ordered on the home page. Each config's `category` field must be one of the `id`s below.
 *
 * Why this exists: the catalogue grew to 50+ calculators across Tasks 14–16, and a flat grid was
 * unusable. Categories are intentionally few and broad (a member should scan ~8 headings, not 15).
 * Earlier configs used overlapping labels ("Income" vs "Wealth & Income", "Insurance" vs
 * "Protection", "Planning"); those were consolidated into the set below.
 *
 * `order` controls display order. `blurb` is the one-line section subheading on the home page.
 */
export const CATEGORIES = [
  {
    id: "Comprehensive & Reports",
    order: 0,
    blurb: "All-in-one views and client-ready multi-page reports.",
  },
  {
    id: "Growth",
    order: 1,
    blurb: "Grow a lumpsum or SIP and see what it becomes.",
  },
  {
    id: "Target-Based",
    order: 2,
    blurb: "Work backwards from a goal to the SIP, STP, or lumpsum you need.",
  },
  {
    id: "Life Goals",
    order: 3,
    blurb: "Plan for education, weddings, a home, a car, or a dream trip.",
  },
  {
    id: "Retirement & Income",
    order: 4,
    blurb: "Build a retirement corpus and draw a steady income from it (SWP).",
  },
  {
    id: "Wealth Strategies",
    order: 5,
    blurb: "Salary, EMIs, rental yield, asset allocation, and getting debt-free.",
  },
  {
    id: "Insurance & Protection",
    order: 6,
    blurb: "Size your cover and compare protection against investment.",
  },
  {
    id: "Tax",
    order: 7,
    blurb: "Capital gains, 54EC bonds, and debt-fund hold-or-sell — indicative only.",
  },
  {
    id: "Ready Reckoners",
    order: 8,
    blurb: "At-a-glance tables across a range of amounts and time periods.",
  },
];

const CATEGORY_ORDER = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.order]));

/**
 * A hand-built page (Debt Freedom) isn't a registry config but should still appear in a category.
 * List such pages here as synthetic entries so the home page can render them alongside configs.
 */
export const EXTRA_PAGES = [
  {
    key: "debt-freedom",
    to: "/debt-freedom",
    title: "Debt Freedom",
    description: "Compare avalanche, snowball, and hybrid strategies to clear your debts.",
    category: "Wealth Strategies",
  },
  {
    key: "report-all-in-one",
    to: "/reports/report-all-in-one",
    title: "All-in-One Investment Proposal",
    description: "Goals, required SIP/lumpsum, asset allocation, protection, and a consolidated projection — as one branded PDF.",
    category: "Comprehensive & Reports",
  },
  {
    key: "report-portfolio-projection",
    to: "/reports/report-portfolio-projection",
    title: "Portfolio Projection Report",
    description: "Project current holdings plus ongoing SIP with a year-by-year table and chart.",
    category: "Comprehensive & Reports",
  },
  {
    key: "report-review-existing",
    to: "/reports/report-review-existing",
    title: "Review of Existing Investment",
    description: "Policy IRR, debt-fund hold-or-sell, and an educational recommendations summary.",
    category: "Comprehensive & Reports",
  },
];

/**
 * Returns the calculators + extra pages grouped by category, in canonical order, each group's
 * items sorted by their position in the registry. Groups with no items are omitted.
 */
export function groupedCalculators() {
  const entries = [
    ...CALCULATORS.map((c) => ({
      key: c.key,
      to: `/calculators/${c.key}`,
      title: c.title,
      description: c.description,
      category: c.category || "Growth",
    })),
    ...EXTRA_PAGES,
  ];

  const groups = new Map();
  for (const entry of entries) {
    if (!groups.has(entry.category)) groups.set(entry.category, []);
    groups.get(entry.category).push(entry);
  }

  return CATEGORIES.filter((cat) => groups.has(cat.id)).map((cat) => ({
    ...cat,
    items: groups.get(cat.id),
  }));
}
